import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta from '@/components/feature/SEOMeta';
import { articleMap, articles } from '@/mocks/articles';
import { categories, categoryMap } from '@/mocks/categories';

const articleSchema = (article: typeof articles[0], catName: string) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: article.title,
  description: article.excerpt,
  author: {
    '@type': 'Person',
    name: article.author,
  },
  datePublished: article.publishDate,
  image: article.imageUrl,
  articleSection: catName,
  publisher: {
    '@type': 'Organization',
    name: '一起草.com',
  },
});

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articleMap[slug] : undefined;

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16">
          <section className="py-20 md:py-28 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
              <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-6">
                <i className="ri-file-search-line text-3xl"></i>
              </div>
              <h1 className="text-2xl font-heading font-bold text-foreground-950">文章未找到</h1>
              <p className="mt-4 text-foreground-600">抱歉，您查找的文章不存在或已被移除。</p>
              <Link
                to="/"
                className="inline-flex mt-6 px-6 py-2.5 rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors font-medium text-sm whitespace-nowrap cursor-pointer"
              >
                返回首页
              </Link>
            </div>
          </section>
        </main>
        <Footer />
      </>
    );
  }

  const cat = categoryMap[article.category];
  const catName = cat?.name || article.category;
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  return (
    <>
      <SEOMeta
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://szarssm.cn/article/${article.slug}`}
        ogType="article"
        ogImage={article.imageUrl}
        schema={articleSchema(article, catName)}
      />

      <Navbar />

      <main className="pt-16">
        {/* Breadcrumb */}
        <section className="bg-background-50 border-b border-background-200">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: catName, to: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>
        </section>

        {/* Article Content */}
        <article className="py-10 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            {/* Header */}
            <header className="mb-10">
              <div className="flex items-center gap-3 text-xs text-foreground-500 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 font-medium hover:bg-primary-200 transition-colors cursor-pointer"
                >
                  {catName}
                </Link>
                <span>{article.publishDate}</span>
                <span>{article.readTime}</span>
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-3 mt-6">
                <div className="w-10 h-10 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center font-semibold text-sm">
                  {article.authorAvatar}
                </div>
                <div>
                  <div className="text-sm font-semibold text-foreground-900">{article.author}</div>
                  <div className="text-xs text-foreground-500">作者</div>
                </div>
              </div>
            </header>

            {/* Featured Image */}
            <div className="aspect-[16/9] rounded-xl overflow-hidden mb-10">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Content */}
            <div
              className="prose prose-sm md:prose-base max-w-none
                prose-headings:font-heading prose-headings:text-foreground-950 prose-headings:font-bold
                prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-5
                prose-h3:text-lg md:prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4
                prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:my-4
                prose-strong:text-foreground-900
                prose-a:text-primary-600 prose-a:no-underline hover:prose-a:underline
                prose-li:text-foreground-700
              "
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Tags */}
            <div className="mt-10 pt-8 border-t border-background-200">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs font-medium"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-16 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-10">
                相关文章
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.slug}
                    to={`/article/${rel.slug}`}
                    className="group rounded-xl overflow-hidden bg-background-50 hover:bg-background-200/50 transition-colors cursor-pointer"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={rel.imageUrl}
                        alt={rel.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <div className="text-xs text-foreground-500 mb-2">
                        {rel.publishDate} · {rel.readTime}
                      </div>
                      <h3 className="text-sm font-heading font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors leading-snug">
                        {rel.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}